import React, { useState } from 'react';
import { 
  Building2, Download, Copy, Check, AlertCircle, ArrowLeft, 
  Sparkles, FileText, Upload, Image as ImageIcon, Trash2, 
  Search, Calculator, Scale, DollarSign, Home, Car, Cpu, Globe, Printer,
  ExternalLink, Maximize2, X, FilePlus, PlusCircle
} from 'lucide-react';
import TelegramExportButton from './TelegramExportButton';

interface PropertyValuatorProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

interface PhotoFile {
  name: string;
  mimeType: string;
  data: string; // Base64
  previewUrl: string;
}

interface DocFile {
  name: string;
  text: string;
}

const PROPERTY_TYPES_UK = [
  "Нерухоме майно: Житло (Квартира, Приватний будинок, Котедж)",
  "Нерухоме майно: Комерційна нерухомість (Офіс, Склад, Торгова площа, Громадська будівля)",
  "Земельна ділянка (Житлова забудова, ОСГ, Комерційне призначення, Промисловість)",
  "Транспортний засіб (Легковий автомобіль, Вантажівка, Спецтехніка, Автоавтобус)",
  "Машини, обладнання та виробничі лінії (Техніка, Інвентар, Верстати)",
  "Цілісний майновий комплекс (ЦМК) / Бізнес / Майнові права"
];

const PROPERTY_TYPES_EN = [
  "Residential Real Estate (Apartment, House, Cottage)",
  "Commercial Real Estate (Office, Warehouse, Retail Space)",
  "Land Plot (Residential, Agricultural, Commercial)",
  "Vehicles & Equipment (Car, Truck, Special Machinery)",
  "Machinery & Industrial Equipment",
  "Integral Property Complex / Business Asset"
];

const VALUATION_PURPOSES_UK = [
  "Оподаткування, продаж та нотаріальне посвідчення угоди",
  "Оформлення застави / кредитування в банку",
  "Бухгалтерський облік, переоцінка основних засобів та амортизація",
  "Судовий розгляд, врегулювання спорів та розподіл майна",
  "Визначення орендної плати / передача в оренду",
  "Приватизація, викуп або внесення до статутного капіталу"
];

const LOADING_STEPS_UK = [
  "Ініціалізація ШІ-оцінювача та завантаження Національних стандартів оцінки (НСО №1, №2, №3)...",
  "Візуальний мультимодальний аналіз завантажених фотографій об'єкта (аналіз стану, оздоблення, зносу)...",
  "Опрацювання технічного паспорта та наданих документів на майно...",
  "Пошук актуальних ринкових аналогів в мережі Інтернет (OLX, RIA, DOM.RIA, ProZorro)...",
  "Розрахунок вартості за порівняльним, дохідним та витратним підходами з коригуванням...",
  "Формування офіційного Звіту про оцінку майна та Висновку про вартість..."
];

const LOADING_STEPS_EN = [
  "Initializing AI Property Valuator & loading National Valuation Standards (NSO #1, #2, #3)...",
  "Multimodal visual analysis of uploaded photos (condition, wear & tear, quality inspection)...",
  "Parsing technical passport and uploaded legal documents...",
  "Online search for real market comparables in Ukraine (OLX, RIA, ProZorro)...",
  "Calculating value using Comparable, Income, and Cost approaches with adjustments...",
  "Generating official Property Valuation Report and Price Conclusion..."
];

export default function PropertyValuator({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: PropertyValuatorProps) {
  const isUk = language === 'uk';
  const propertyTypes = isUk ? PROPERTY_TYPES_UK : PROPERTY_TYPES_EN;
  const valuationPurposes = isUk ? VALUATION_PURPOSES_UK : [];

  const [propertyType, setPropertyType] = useState(propertyTypes[0]);
  const [location, setLocation] = useState('м. Київ, Печерський район');
  const [specifications, setSpecifications] = useState('');
  const [valuationPurpose, setValuationPurpose] = useState(valuationPurposes[0] || 'Оподаткування та нотаріальне оформлення');
  const [searchComparables, setSearchComparables] = useState(true);

  // Files state
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [documents, setDocuments] = useState<DocFile[]>([]);
  const [isExtractingDoc, setIsExtractingDoc] = useState(false);

  // Results state
  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  const [copiedReport, setCopiedReport] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [activeScreenshot, setActiveScreenshot] = useState<string | null>(null);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 6000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk]);

  // Handle Photo Upload
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach((file: File) => {
      if (!file.type.startsWith('image/')) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        if (result) {
          setPhotos((prev) => [
            ...prev,
            {
              name: file.name,
              mimeType: file.type,
              data: result,
              previewUrl: result
            }
          ]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle Document Upload
  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsExtractingDoc(true);
    setError(null);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch('/api/extract-text', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `Не вдалося вилучити текст з ${file.name}`);
        }

        const data = await response.json();
        if (data.text) {
          setDocuments((prev) => [
            ...prev,
            {
              name: file.name,
              text: data.text
            }
          ]);
        }
      } catch (err: any) {
        console.error(err);
        setError(err.message || `Помилка обробки документа ${file.name}`);
      }
    }

    setIsExtractingDoc(false);
  };

  const removeDocument = (index: number) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  // Run Valuation
  const handleRunValuation = async () => {
    if (!specifications.trim()) {
      setError(isUk ? 'Будь ласка, вкажіть характеристики об\'єкта оцінки.' : 'Please describe property specifications.');
      return;
    }

    setIsAuditing(true);
    setError(null);
    setReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const combinedDocsText = documents.map((d) => `--- ФАЙЛ: ${d.name} ---\n${d.text}`).join('\n\n');

      const response = await fetch('/api/evaluate-property', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          propertyType,
          location,
          specifications,
          valuationPurpose,
          photos: photos.map((p) => ({ mimeType: p.mimeType, data: p.data })),
          documentsText: combinedDocsText,
          searchComparables,
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка проведення оцінки майна.' : 'Failed to evaluate property.'));
      }

      const data = await response.json();
      if (data.report) {
        setReport(data.report);
      } else {
        throw new Error(isUk ? 'Відповідь не містить звіту.' : 'Invalid valuation response.');
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка.' : 'An error occurred.'));
    } finally {
      setIsAuditing(false);
    }
  };

  // Download Word (.docx)
  const handleDownloadWord = async () => {
    if (!report) return;

    try {
      const name = "Zvit_Otsinka_Maina_" + propertyType.replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, "_");
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: report,
          fileName: name
        })
      });

      if (!response.ok) throw new Error(isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word file.');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err: any) {
      alert(err.message || (isUk ? 'Помилка завантаження Word.' : 'Error downloading Word.'));
    }
  };

  const handleCopyReport = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleNewReport = () => {
    setReport(null);
    setError(null);
    setSpecifications('');
    setPhotos([]);
    setDocuments([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Extract Summary Cards Data from Report Text
  const extractSummaryData = (text: string) => {
    const uahMatch = text.match(/Ринкова вартість у національній валюті[^\n]*?\*\*([^*]+)\*\*/i) ||
                     text.match(/Ринкова вартість[^\n]*?\*\*([^*]+грн[^*]*)\*\*/i) ||
                     text.match(/([0-9\s,]{4,}\s*(?:грн|UAH))/i);

    const usdMatch = text.match(/Еквівалент у валюті США[^\n]*?\*\*([^*]+)\*\*/i) ||
                     text.match(/(\$[0-9\s,]{3,})/i) ||
                     text.match(/([0-9\s,]{3,}\s*USD)/i);

    const unitMatch = text.match(/Питома вартість[^\n]*?\*\*([^*]+)\*\*/i) ||
                      text.match(/([0-9\s,]+\s*(?:грн|\$)\/(?:кв\.м|м²|од\.))/i);

    return {
      totalUah: uahMatch ? uahMatch[1] : null,
      totalUsd: usdMatch ? usdMatch[1] : null,
      unitPrice: unitMatch ? unitMatch[1] : null
    };

  const summary = report ? extractSummaryData(report) : null;

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">
      {/* Navigation Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700 font-mono"
          >
            🏠 {isUk ? 'На головну' : 'Home'}
          </button>
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700 font-mono"
          >
            <ArrowLeft className="w-4 h-4 text-blue-400" />
            <span>{isUk ? '← До Будівельного Центру' : '← Back to Construction Hub'}</span>
          </button>
        </div>

        {useEnterpriseContext && enterpriseName && (
          <span className="px-3 py-1 bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs rounded-full">
            {isUk ? `Контекст: ${enterpriseName}` : `Context: ${enterpriseName}`}
          </span>
        )}
      </div>

      {/* Main Title Banner */}
      <div className="bg-gradient-to-r from-amber-950/40 via-slate-900 to-slate-900 border border-amber-500/20 rounded-2xl p-6 sm:p-8 backdrop-blur-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
        
        <div className="flex items-start space-x-4 relative z-10">
          <div className="p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-2xl text-amber-400">
            <Building2 className="w-8 h-8" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold text-white">
                {isUk ? 'Оцінювач майна & Аналітик ринку' : 'Property & Asset Valuation Agent'}
              </h1>
              <span className="px-2.5 py-0.5 bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-semibold rounded-full">
                НСО №1, №2, №3
              </span>
            </div>
            <p className="text-slate-300 text-sm max-w-3xl leading-relaxed">
              {isUk
                ? 'Експертна оцінка майна відповідно до Національних стандартів оцінки України (НСО №1, №2, №3) та Постанов КМУ №1440, №1442. Здійснює візуальний аналіз фотографій об\'єкта, вилучає дані з документів, шукає ринкові аналоги в мережі Інтернет та формує розрахований Звіт про оцінку майна з висновком про вартість.'
                : 'Expert AI Property Valuator under National Valuation Standards of Ukraine (NSO #1, #2, #3). Analyzes property photos visually, extracts doc data, researches market comparables online, and issues an official Valuation Report with price conclusion.'}
            </p>
          </div>
        </div>
      </div>

      {/* Form Input Section */}
      {!report && (
        <div className="space-y-6">
          <button
            type="button"
            onClick={() => {
              setPropertyType(isUk ? 'Комерційна нерухомість (офіс/склад)' : 'Commercial Property');
              setLocation("м. Київ, Шевченківський р-н, вул. Жилянська");
              setValuationPurpose(isUk ? 'Продаж / Купівля' : 'Sale / Purchase');
              setSpecifications(`Складсько-офісний комплекс загальною площею 1 450 кв.м. (складська частина 1 100 кв.м, висота стелі 7м, рампа для вантажівок; офісна частина 350 кв.м з євроремонтом).
Земельна ділянка 0.45 га в оренді. Заведена потужність електромережі 150 кВт. Автономне газове опалення.`);
              setError(null);
            }}
            className="w-full py-2.5 px-4 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 rounded-xl text-emerald-300 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
          >
            <Sparkles className="w-4 h-4 text-emerald-400 animate-pulse" />
            <span>{isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (Оцінка комерційного комплексу 1 450 кв.м у Києві)" : "⚡ Try 1-click Demo Sample (Valuation of Commercial Property 1450 sq.m)"}</span>
          </button>

          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-6 backdrop-blur-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Property Type Dropdown */}
            <div>
              <label className="block text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">
                {isUk ? '1. Тип об\'єкта оцінки' : '1. Asset Type'}
              </label>
              <select
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-amber-500 transition-colors"
              >
                {propertyTypes.map((type, idx) => (
                  <option key={idx} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Location */}
            <div>
              <label className="block text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">
                {isUk ? '2. Локація / Адреса об\'єкта' : '2. Location / Address'}
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder={isUk ? 'напр. м. Київ, Печерський район, вул. Хрещатик' : 'e.g. Kyiv, Pechersk district'}
                className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-amber-500 transition-colors"
              />
            </div>
          </div>

          {/* Valuation Purpose & Internet Search */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">
                {isUk ? '3. Мета проведення оцінки' : '3. Purpose of Valuation'}
              </label>
              <select
                value={valuationPurpose}
                onChange={(e) => setValuationPurpose(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-amber-500 transition-colors"
              >
                {valuationPurposes.map((purpose, idx) => (
                  <option key={idx} value={purpose}>
                    {purpose}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center">
              <label className="flex items-center space-x-3 cursor-pointer p-3 bg-slate-950 border border-slate-800 rounded-xl w-full hover:border-amber-500/40 transition-colors">
                <input
                  type="checkbox"
                  checked={searchComparables}
                  onChange={(e) => setSearchComparables(e.target.checked)}
                  className="w-4 h-4 rounded text-amber-500 border-slate-700 focus:ring-amber-500"
                />
                <div>
                  <span className="text-sm font-medium text-slate-200 flex items-center gap-1.5">
                    <Globe className="w-4 h-4 text-amber-400" />
                    {isUk ? 'Онлайн-пошук аналогів на ринку' : 'AI Live Search Market Comparables'}
                  </span>
                  <p className="text-xs text-slate-400">
                    {isUk ? 'Автоматичний пошук свіжих пропозицій на OLX, RIA, ProZorro' : 'Fetch live market listings in Ukraine'}
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Detailed Property Specifications Textarea */}
          <div>
            <label className="block text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">
              {isUk ? '4. Специфікація та характеристики майна' : '4. Property Specifications & Features'}
            </label>
            <textarea
              rows={4}
              value={specifications}
              onChange={(e) => setSpecifications(e.target.value)}
              placeholder={isUk 
                ? 'Опишіть майно: загальна/житлова площа (кв.м), поверховість/поверх, матеріал стін, рік побудови/випуску, стан ремонту, інженерні мережі, дефекти, планомірність тощо...'
                : 'Describe property specs: total area (sq.m), floor, building materials, construction year, renovation status, utilities...'}
              className="w-full bg-slate-950 border border-slate-700/80 rounded-xl p-4 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-amber-500 transition-colors"
            />
          </div>

          {/* Photos Upload & Previews */}
          <div>
            <label className="block text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-amber-400" />
              {isUk ? '5. Завантаження фотографій об\'єкта (Візуальний аналіз стан/дефекти)' : '5. Upload Property Photos (Multimodal Inspection)'}
            </label>

            <div className="flex items-center gap-4">
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 bg-slate-950 hover:bg-slate-800 border border-slate-700 rounded-xl text-sm font-medium text-slate-200 transition-colors">
                <Upload className="w-4 h-4 text-amber-400" />
                {isUk ? 'Обрати фотографії (JPG/PNG)' : 'Upload Photos'}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </label>

              <span className="text-xs text-slate-400">
                {photos.length > 0 ? (isUk ? `Завантажено ${photos.length} фото` : `${photos.length} photos uploaded`) : (isUk ? 'Фото не обрано' : 'No photos added')}
              </span>
            </div>

            {/* Photos Preview Grid */}
            {photos.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3 mt-4">
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative group rounded-xl overflow-hidden border border-slate-700 bg-slate-950 aspect-square">
                    <img src={photo.previewUrl} alt={photo.name} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(idx)}
                      className="absolute top-1 right-1 p-1 bg-red-600/80 hover:bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <span className="absolute bottom-0 inset-x-0 bg-slate-950/80 text-[10px] text-slate-300 px-1 py-0.5 truncate text-center">
                      {photo.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Documents Upload Section */}
          <div>
            <label className="block text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-amber-400" />
              {isUk ? '6. Завантаження документів (Технічний паспорт, витяг з ДРРП, Акт)' : '6. Upload Documents (PDF/Word/TXT)'}
            </label>

            <div className="flex items-center gap-4">
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 bg-slate-950 hover:bg-slate-800 border border-slate-700 rounded-xl text-sm font-medium text-slate-200 transition-colors">
                <Upload className="w-4 h-4 text-amber-400" />
                {isUk ? 'Додати документи (PDF, DOCX, TXT)' : 'Upload Documents'}
                <input
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  multiple
                  onChange={handleDocumentUpload}
                  className="hidden"
                />
              </label>

              {isExtractingDoc && (
                <span className="text-xs text-amber-400 flex items-center gap-1.5 animate-pulse">
                  <Sparkles className="w-3.5 h-3.5" />
                  {isUk ? 'Зчитування тексту документів...' : 'Extracting document text...'}
                </span>
              )}
            </div>

            {/* Uploaded Docs Badge List */}
            {documents.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-slate-950 border border-slate-700/80 px-3 py-1.5 rounded-lg text-xs text-slate-300">
                    <FileText className="w-3.5 h-3.5 text-amber-400" />
                    <span className="truncate max-w-xs">{doc.name}</span>
                    <button onClick={() => removeDocument(idx)} className="text-slate-500 hover:text-red-400 transition-colors">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center space-x-3 text-red-400 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Submit Action Button */}
          <div className="pt-2">
            <button
              onClick={handleRunValuation}
              disabled={isAuditing}
              className="w-full py-4 bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 hover:from-amber-500 hover:to-amber-500 text-slate-950 font-bold rounded-xl shadow-lg shadow-amber-500/20 flex items-center justify-center space-x-2 transition-all disabled:opacity-50"
            >
              {isAuditing ? (
                <>
                  <Sparkles className="w-5 h-5 animate-spin" />
                  <span>{isUk ? 'Проведення оцінки майна...' : 'Evaluating Property...'}</span>
                </>
              ) : (
                <>
                  <Calculator className="w-5 h-5" />
                  <span>{isUk ? 'Сформувати Звіт про оцінку майна' : 'Generate Property Valuation Report'}</span>
                </>
              )}
            </button>
          </div>

          {/* Loading Indicator Steps */}
          {isAuditing && (
            <div className="p-6 bg-slate-950/80 border border-amber-500/20 rounded-xl space-y-3 animate-pulse">
              <div className="flex items-center space-x-3 text-amber-400 text-sm font-semibold">
                <Sparkles className="w-5 h-5 animate-spin" />
                <span>
                  {isUk ? (LOADING_STEPS_UK[loadingStepIndex] || LOADING_STEPS_UK[0]) : (LOADING_STEPS_EN[loadingStepIndex] || LOADING_STEPS_EN[0])}
                </span>
              </div>
              <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-amber-500 to-amber-300 h-full transition-all duration-500"
                  style={{ width: `${((loadingStepIndex + 1) / (isUk ? LOADING_STEPS_UK.length : LOADING_STEPS_EN.length)) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
        </div>
      )}

      {/* Generated Report Display */}
      {report && (
        <div className="space-y-6">
          {/* Executive Summary Price Cards */}
          {summary && (summary.totalUah || summary.totalUsd) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-900/80 border border-amber-500/30 rounded-2xl p-5 backdrop-blur-xl">
                <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">
                  {isUk ? 'Ринкова вартість (UAH)' : 'Market Value (UAH)'}
                </span>
                <p className="text-2xl font-black text-amber-300 mt-1">
                  {summary.totalUah || '—'}
                </p>
                <span className="text-xs text-slate-400 mt-1 block">
                  {isUk ? 'Розраховано за НСО №1 та №2' : 'Calculated under NSO #1 & #2'}
                </span>
              </div>

              <div className="bg-slate-900/80 border border-amber-500/30 rounded-2xl p-5 backdrop-blur-xl">
                <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">
                  {isUk ? 'Еквівалент (USD)' : 'Equivalent (USD)'}
                </span>
                <p className="text-2xl font-black text-emerald-400 mt-1">
                  {summary.totalUsd || '—'}
                </p>
                <span className="text-xs text-slate-400 mt-1 block">
                  {isUk ? 'За курсом НБУ / ринковим' : 'At market exchange rate'}
                </span>
              </div>

              <div className="bg-slate-900/80 border border-amber-500/30 rounded-2xl p-5 backdrop-blur-xl">
                <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">
                  {isUk ? 'Питома вартість' : 'Unit Price'}
                </span>
                <p className="text-xl font-bold text-slate-200 mt-1">
                  {summary.unitPrice || '—'}
                </p>
                <span className="text-xs text-slate-400 mt-1 block">
                  {isUk ? 'Вартість за 1 кв.м / одиницю' : 'Price per sq.m / unit'}
                </span>
              </div>
            </div>
          )}

          {/* Report Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-900/80 border border-slate-800 p-4 rounded-xl backdrop-blur-xl print:hidden">
            <button
              onClick={() => setReport(null)}
              className="inline-flex items-center text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1" />
              {isUk ? 'Оцінити інше майно' : 'Valuate another asset'}
            </button>

            <div className="flex flex-wrap items-center gap-2">
              <TelegramExportButton
                title={`Звіт про оцінку майна: ${propertyType}`}
                content={report || ''}
                agentName="Оцінювач Майна (НСО №1 & №2)"
                agentId="property-valuator"
                isUk={isUk}
                variant="primary"
              />

              <button
                onClick={handleCopyReport}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-medium inline-flex items-center space-x-1.5 transition-colors"
              >
                {copiedReport ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedReport ? (isUk ? 'Скопійовано' : 'Copied') : (isUk ? 'Скопіювати' : 'Copy')}</span>
              </button>

              <button
                onClick={handlePrint}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-medium inline-flex items-center space-x-1.5 transition-colors"
              >
                <Printer className="w-3.5 h-3.5 text-amber-400" />
                <span>{isUk ? 'Друк / PDF' : 'Print / PDF'}</span>
              </button>

              <button
                onClick={handleDownloadWord}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg text-xs font-bold inline-flex items-center space-x-1.5 transition-colors shadow-lg shadow-amber-500/20"
              >
                <Download className="w-3.5 h-3.5" />
                <span>{isUk ? 'Завантажити Word (.docx)' : 'Download Word'}</span>
              </button>

              <button
                onClick={handleNewReport}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold inline-flex items-center space-x-1.5 transition-colors shadow-lg shadow-emerald-600/20"
              >
                <FilePlus className="w-3.5 h-3.5" />
                <span>{isUk ? 'Новий звіт' : 'New Report'}</span>
              </button>
            </div>
          </div>

          {/* Formatted Valuation Report Content */}
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 sm:p-10 text-slate-200 leading-relaxed space-y-4 backdrop-blur-xl print:bg-white print:text-black print:p-0 print:border-0">
            <div className="prose prose-invert max-w-none prose-headings:text-amber-300 prose-a:text-amber-400 prose-table:border-slate-700 print:prose-neutral">
              {(() => {
                const renderFormattedText = (text: string) => {
                  if (!text) return null;

                  const mdLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s\)]+)\)/g;
                  const parts: React.ReactNode[] = [];
                  let lastIndex = 0;
                  let match;

                  while ((match = mdLinkRegex.exec(text)) !== null) {
                    const [fullMatch, linkTitle, linkUrl] = match;
                    const matchIndex = match.index;

                    if (matchIndex > lastIndex) {
                      parts.push(text.substring(lastIndex, matchIndex));
                    }

                    parts.push(
                      <a
                        key={matchIndex}
                        href={linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 font-semibold underline underline-offset-2 hover:bg-amber-500/10 px-1.5 py-0.5 rounded transition-colors my-0.5"
                      >
                        <span>{linkTitle}</span>
                        <ExternalLink className="w-3.5 h-3.5 shrink-0 text-amber-400" />
                      </a>
                    );

                    lastIndex = matchIndex + fullMatch.length;
                  }

                  if (lastIndex < text.length) {
                    parts.push(text.substring(lastIndex));
                  }

                  return parts.length > 0 ? parts : text;
                };

                const lines = report.split('\n');
                const elements: React.ReactNode[] = [];
                let i = 0;

                while (i < lines.length) {
                  const line = lines[i];

                  if (line.startsWith('# ')) {
                    elements.push(<h1 key={i} className="text-2xl font-bold text-amber-300 border-b border-amber-500/30 pb-3 mt-6 mb-4">{renderFormattedText(line.replace('# ', ''))}</h1>);
                    i++;
                    continue;
                  }
                  if (line.startsWith('## ')) {
                    elements.push(<h2 key={i} className="text-lg font-bold text-amber-400 mt-6 mb-3 border-b border-slate-800 pb-2">{renderFormattedText(line.replace('## ', ''))}</h2>);
                    i++;
                    continue;
                  }
                  if (line.startsWith('### ')) {
                    elements.push(<h3 key={i} className="text-base font-semibold text-slate-200 mt-5 mb-2">{renderFormattedText(line.replace('### ', ''))}</h3>);
                    i++;
                    continue;
                  }
                  if (line.startsWith('#### ')) {
                    elements.push(<h4 key={i} className="text-sm font-semibold text-amber-300/90 mt-4 mb-2">{renderFormattedText(line.replace('#### ', ''))}</h4>);
                    i++;
                    continue;
                  }

                  // Markdown Image: ![alt](src)
                  const imgMatch = line.match(/^!\[(.*?)\]\((.*?)\)/);
                  if (imgMatch) {
                    const altText = imgMatch[1] || "Скриншот сторінки аналога";
                    const imgSrc = imgMatch[2];
                    elements.push(
                      <div key={i} className="my-5 bg-slate-950/80 border border-slate-700/80 rounded-2xl p-4 space-y-3 group hover:border-amber-500/40 transition-all shadow-xl print:hidden">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-amber-400 flex items-center gap-1.5">
                            <ImageIcon className="w-4 h-4 text-amber-400" />
                            {altText}
                          </span>
                          <button
                            type="button"
                            onClick={() => setActiveScreenshot(imgSrc)}
                            className="text-xs text-slate-300 hover:text-white flex items-center gap-1 bg-slate-900 px-3 py-1.5 rounded-lg border border-slate-700 hover:border-amber-500/50 transition-colors shadow-sm"
                          >
                            <Maximize2 className="w-3.5 h-3.5 text-amber-400" />
                            <span>{isUk ? 'Збільшити скриншот' : 'Enlarge Screenshot'}</span>
                          </button>
                        </div>
                        <div 
                          onClick={() => setActiveScreenshot(imgSrc)}
                          className="relative overflow-hidden rounded-xl cursor-pointer border border-slate-800 bg-slate-900 max-h-[450px]"
                        >
                          <img
                            src={imgSrc}
                            alt={altText}
                            className="w-full h-auto object-cover object-top group-hover:scale-[1.01] transition-transform duration-300"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <span className="px-4 py-2 bg-slate-900/95 border border-amber-500/50 text-amber-300 text-xs font-bold rounded-xl shadow-2xl backdrop-blur-md flex items-center gap-2">
                              <Maximize2 className="w-4 h-4" />
                              {isUk ? 'Натисніть для відкриття на весь екран' : 'Click for Full Screen'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                    i++;
                    continue;
                  }

                  if (line.startsWith('- ')) {
                    elements.push(<li key={i} className="ml-4 list-disc text-slate-300 my-1.5 leading-relaxed">{renderFormattedText(line.replace('- ', ''))}</li>);
                    i++;
                    continue;
                  }

                  // Markdown Table Block
                  if (line.startsWith('|')) {
                    const tableLines: string[] = [];
                    while (i < lines.length && lines[i].startsWith('|')) {
                      tableLines.push(lines[i]);
                      i++;
                    }

                    const rows = tableLines
                      .filter(l => !l.includes('---|'))
                      .map(l => l.split('|').slice(1, -1).map(c => c.trim()));

                    if (rows.length > 0) {
                      const header = rows[0];
                      const body = rows.slice(1);

                      elements.push(
                        <div key={i} className="my-5 overflow-x-auto rounded-xl border border-slate-800 bg-slate-950/80 shadow-lg">
                          <table className="w-full text-xs text-left text-slate-300">
                            <thead className="bg-slate-900/90 text-amber-400 font-semibold border-b border-slate-800 uppercase tracking-wider">
                              <tr>
                                {header.map((col, cIdx) => (
                                  <th key={cIdx} className="px-4 py-3 border-r border-slate-800 last:border-r-0">
                                    {renderFormattedText(col)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/60">
                              {body.map((row, rIdx) => (
                                <tr key={rIdx} className="hover:bg-slate-900/50 transition-colors">
                                  {row.map((cell, cIdx) => (
                                    <td key={cIdx} className="px-4 py-3 border-r border-slate-800/60 last:border-r-0">
                                      {renderFormattedText(cell)}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }
                    continue;
                  }

                  if (line.trim() === '---') {
                    elements.push(<hr key={i} className="border-slate-800 my-6" />);
                    i++;
                    continue;
                  }

                  if (line.trim()) {
                    elements.push(<p key={i} className="text-slate-300 text-sm leading-relaxed my-2">{renderFormattedText(line)}</p>);
                  }

                  i++;
                }

                return elements;
              })()}
            </div>
          </div>

          {/* Bottom Action Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-900/80 border border-slate-800 p-4 rounded-xl backdrop-blur-xl print:hidden">
            <button
              onClick={handleNewReport}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold inline-flex items-center space-x-1.5 transition-colors shadow-lg shadow-emerald-600/20"
            >
              <FilePlus className="w-3.5 h-3.5" />
              <span>{isUk ? 'Створити новий звіт' : 'Create New Report'}</span>
            </button>

            <div className="flex flex-wrap items-center gap-2">
              <TelegramExportButton
                title={`Звіт про оцінку майна: ${propertyType}`}
                content={report || ''}
                agentName="Оцінювач Майна (НСО №1 & №2)"
                agentId="property-valuator"
                isUk={isUk}
                variant="primary"
              />

              <button
                onClick={handlePrint}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-medium inline-flex items-center space-x-1.5 transition-colors"
              >
                <Printer className="w-3.5 h-3.5 text-amber-400" />
                <span>{isUk ? 'Друк / PDF' : 'Print / PDF'}</span>
              </button>

              <button
                onClick={handleDownloadWord}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg text-xs font-bold inline-flex items-center space-x-1.5 transition-colors shadow-lg shadow-amber-500/20"
              >
                <Download className="w-3.5 h-3.5" />
                <span>{isUk ? 'Завантажити Word (.docx)' : 'Download Word'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal for Screenshots */}
      {activeScreenshot && (
        <div 
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 animate-fadeIn"
          onClick={() => setActiveScreenshot(null)}
        >
          <div 
            className="relative max-w-6xl w-full max-h-[92vh] bg-slate-900 border border-amber-500/30 rounded-2xl p-4 overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center pb-3 border-b border-slate-800">
              <span className="text-sm font-bold text-amber-300 flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-amber-400" />
                {isUk ? 'Скриншот веб-сторінки джерела аналога' : 'Analog Source Web Page Screenshot'}
              </span>
              <button
                type="button"
                onClick={() => setActiveScreenshot(null)}
                className="p-1.5 text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-auto flex-1 mt-3 flex items-center justify-center bg-slate-950 rounded-xl p-2">
              <img 
                src={activeScreenshot} 
                alt="Screenshot" 
                className="max-w-full h-auto rounded-lg object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
}


